import { isHomeInsuranceSlug } from '@/utils/homeInsurance';
import {
  isTpvcaMandatorySlug,
  TPVCA_MANDATORY_SLUG,
} from '@/utils/tpvcaInsurance';
import { isKaskoInsuranceSlug, KASKO_INSURANCE_SLUG } from '@/utils/kaskoInsurance';
import { isTravelInsuranceSlug } from '@/utils/travelInsurance';
/**
 * Utility functions for formatting insurance data for contact forms
 */

import {
  getTravelPurposeLabel,
  getTravelActivityLabel,
  getVehicleUsageLabel,
  isCompanyCodeFromValue,
} from '@/app/skaiciuokle/[insurance]/steps/insuranceTypes/constants';
import { isPcaInsuranceSlug } from '@/utils/pcaInsurance';
import { isConstructionCarSlug } from '@/utils/calculatorProductSlugs';
import { isEScooterInsuranceSlug } from '@/utils/bicycleInsurance';

/**
 * Formats insurance form data into a nicely formatted plain text message (for emails)
 * @param {Object} formData - The insurance form data object
 * @param {string|Object} insuranceType - Type of insurance (auto, kasko, tpvca, etc.) or insurance type object
 * @param {string} userComments - Optional user comments to include
 * @returns {string} Formatted plain text string
 */
export const formatInsuranceDataForMessage = (
  formData,
  insuranceType = 'quote_request',
  userComments = ''
) => {
  // Handle both string and object insurance types
  const insuranceTypeObj =
    typeof insuranceType === 'object' ? insuranceType : { type: insuranceType };
  const insuranceTypeString = insuranceTypeObj.type || insuranceType;

  // Normalize insurance type string (handle both 'statybos' and 'statybu-draudimas')
  const normalizedType = insuranceTypeString.replace('-draudimas', '');

  const isPcaInsurance =
    normalizedType === 'pca' ||
    isPcaInsuranceSlug(insuranceTypeString) ||
    isPcaInsuranceSlug(insuranceTypeObj.slug);

  const isConstructionWorkDateLabels =
    isPcaInsurance ||
    normalizedType === 'statybos' ||
    isConstructionCarSlug(insuranceTypeString) ||
    isConstructionCarSlug(insuranceTypeObj.slug);

  const insuranceTypeLabel =
    normalizedType === 'auto' ||
    isTpvcaMandatorySlug(insuranceTypeString)
      ? 'Automobilių draudimas'
      : normalizedType === 'kasko' ||
          isKaskoInsuranceSlug(insuranceTypeString)
        ? 'KASKO draudimas'
        : insuranceTypeString === 'tpvca'
          ? 'TPVCA draudimas'
          : normalizedType === 'statybos' ||
              isConstructionCarSlug(insuranceTypeString) ||
              isConstructionCarSlug(insuranceTypeObj.slug)
            ? 'Statybų (CAR) draudimas'
            : normalizedType === 'travel' ||
                isTravelInsuranceSlug(insuranceTypeString)
              ? 'Kelionių draudimas'
              : insuranceTypeObj.isHomeInsuranceSlug(slug) ||
                  insuranceTypeString === 'nt'
                ? 'Būsto draudimas'
                : insuranceTypeString === 'health'
                  ? 'Sveikatos draudimas'
                  : insuranceTypeString === 'accident'
                    ? 'Nelaimingų atsitikimų draudimas'
                    : isPcaInsurance
                      ? 'Statybos techninio prižiūrėtojo (PCA) draudimas'
                      : isEScooterInsuranceSlug(insuranceTypeObj.slug)
                        ? 'El. paspirtukų draudimas'
                        : insuranceTypeString === 'bikes'
                          ? 'Dviračių ir el. dviračių draudimas'
                          : insuranceTypeString === 'ticks'
                            ? 'Erkių ligų draudimas'
                            : 'Draudimo užklausa';

  let message = `=== ${insuranceTypeLabel} - Užklausa ===\n\n`;

  // Field mapping for display labels
  const fieldLabels = {
    // Vehicle fields
    plate: 'Valstybinis numeris',
    vin: 'Identifikavimo numeris (VIN)',
    code: 'Asmens/įmonės kodas',
    startDate: isConstructionWorkDateLabels
      ? normalizedType === 'statybos' ||
        isConstructionCarSlug(insuranceTypeString)
        ? 'Darbų pradžios data (RETRO data)'
        : 'Darbų pradžios data'
      : 'Draudimo įsigaliojimo data',
    endDate: isConstructionWorkDateLabels
      ? 'Darbų pabaigos data'
      : 'Draudimo pabaigos data',
    work_duration_years: 'Draudimo trukmė',
    registeredVehicle: 'Registruota transporto priemonė',
    vehicle_motor_power_kw: 'Kilovatai (kW)',
    vehicle_engine_capacity_cc: 'Variklio tūris (cm3)',

    // Construction fields
    building_type: 'Pastato tipas',
    construction_material: 'Statybos būdas',
    construction_material_other: 'Kitas statybos būdas',
    work_value: 'Darbų vertė (EUR)',
    buildings_count: 'Pastatų skaičius',
    construction_type: 'Statybos tipas',
    technical_supervisor_certificate_number:
      'Techninio prižiūrėtojo atestato numeris',
    variant: 'Draudimo variantas',
    has_common_walls: 'Ar yra bendros sienos?',
    is_heritage_area: 'Ar yra paveldo teritorija?',
    includes_demolition: 'Ar apima nugriovimą?',

    // Travel fields
    travel_destinations: 'Kelionės šalys',
    travel_start_date: 'Kelionės pradžios data',
    travel_end_date: 'Kelionės pabaigos data',
    travel_purpose: 'Kelionės tikslas',
    travel_activity: 'Veikla kelionės metu',
    insured_travelers: 'Apdraudžiamų keliautojų asmens kodai',

    // Bicycle fields
    bicycle_value: isEScooterInsuranceSlug(insuranceTypeObj.slug)
      ? 'Pasirinkite el. paspirtuko vertę'
      : 'Pasirinkite dviračio vertę',
    bicycle_custom_value: isEScooterInsuranceSlug(insuranceTypeObj.slug)
      ? 'El. paspirtuko vertė (EUR)'
      : 'Dviračio vertė (EUR)',
    additional_insurance_child_seat: 'Vaikiška kėdutė',
    additional_insurance_family_members: 'Šeimos nariams',
    additional_insurance_europe_14_days: 'Galiojimas Europoje iki 14 d.',
    additional_insurance_wheels_saddle: isEScooterInsuranceSlug(
      insuranceTypeObj.slug
    )
      ? 'Ratų ir vairo draudimas'
      : 'Ratų ir balno draudimas',

    // Policyholder fields
    'policyholder.personal_code': 'Draudėjo asmens/įmonės kodas',
    'policyholder.company_code': 'Draudėjo įmonės kodas',
    'policyholder.company_name': 'Draudėjo įmonės pavadinimas',
    'policyholder.address.city': 'Draudėjo miestas',
    'policyholder.address.street': 'Draudėjo gatvė',
    'policyholder.address.house_number': 'Draudėjo namo numeris',
    'policyholder.address.apartment_number': 'Draudėjo buto numeris',

    // Real estate (NT)
    residence_type: 'Nuolat / nenuolat gyvenamas',
    rental_type: 'Nuomos tipas',
    insurance_variant: 'Draudimo variantas',
    is_interior_property_relevant: 'Ar aktualus vidaus turtas',
    is_internal_damages_needed: 'Ar reikalingi vidiniai gedimai',
    is_smart_devices_relevant: 'Ar aktualūs išmanieji įrenginiai',
    is_civil_liability_relevant: 'Ar aktuali civilinė atsakomybė',

    // Construction address fields
    'construction_address.city': 'Statybos miestas',
    'construction_address.street': 'Statybos gatvė',
    'construction_address.house_number': 'Statybos namo numeris',
  };

  // Value mappings for enum fields
  const valueMappings = {
    building_type: {
      SINGLE_FAMILY: 'Vienbutis',
      TWO_FAMILY: 'Dvibutis',
      MULTI_FAMILY: 'Daugiabutis',
      MULTIFAMILY: 'Daugiabutis',
      OTHER: 'Kita',
      COMMERCIAL_PURPOSE: 'Komercinės paskirties',
      COMMERCIAL: 'Komercinė paskirtis',
      INDUSTRIAL: 'Pramoninis pastatas',
    },
    construction_material: {
      MASONRY: 'Mūras (plytos)',
      BLOCKS: 'Blokeliai (silikatiniai / keramzitbetonio / akyto betono)',
      MONOLITH: 'Monolitas (gelžbetonis)',
      METAL_STRUCTURES: 'Metalo konstrukcijos / daugiasluoksnės plokštės',
      OTHER: 'Kita',
    },
    construction_type: {
      NEW_BUILD: 'Nauja statyba',
      RENOVATION: 'Renovacija',
      RECONSTRUCTION: 'Rekonstrukcija',
      CAPITAL_REPAIR: 'Kapitalinis remontas',
      MODERNIZATION: 'Modernizavimas',
      DEMOLITION: 'Griovimas',
    },
    work_duration_years: {
      '1': '1 metai',
      '2': '2 metai',
    },
    bicycle_value: {
      '0-500': '0 - 500 €',
      '501-1000': '501 - 1000 €',
      '1001-2000': '1001 - 2000 €',
      OTHER: 'Kita',
    },
    residence_type: {
      PERMANENT: 'Nuolat gyvenamas',
      NON_PERMANENT: 'Nenuolat gyvenamas',
    },
    rental_type: {
      NONE: 'Nenuomojama',
      LONG_TERM: 'Ilgalaikė nuoma',
      SHORT_TERM: 'Trumpalaikė nuoma',
    },
    insurance_variant: {
      BASIC: 'Pagrindinių sąlygų (ugnis, vanduo, gamtinės jėgos) draudimas',
      EXTENDED: 'Išplėstinis visų rizikų draudimas',
    },
  };

  // Helper function to format field values
  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return 'Nenurodytas';
    }

    if (typeof value === 'boolean') {
      return value ? 'Taip' : 'Ne';
    }

    if (key === 'travel_purpose') {
      return getTravelPurposeLabel(value);
    }
    if (key === 'travel_activity') {
      return getTravelActivityLabel(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'Nenurodytas';
      }
      // For file arrays, show count
      if (
        value[0] instanceof File ||
        (typeof value[0] === 'object' && value[0].name)
      ) {
        return `${value.length} failas(-ai)`;
      }
      // For insured_travelers, filter out empty values
      if (key === 'insured_travelers') {
        const validTravelers = value.filter(t => t && t.trim() !== '');
        if (validTravelers.length === 0) {
          return 'Nenurodytas';
        }
        return validTravelers.join(', ');
      }
      // For travel destinations, format as country names
      if (key === 'travel_destinations' && typeof value[0] === 'string') {
        return value.join(', ');
      }
      return value.join(', ');
    }

    // Check if there's a mapping for this field
    if (valueMappings[key] && valueMappings[key][value]) {
      return valueMappings[key][value];
    }

    if (typeof value === 'object') {
      // Handle nested objects
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Helper to get nested value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Build message dynamically from formData
  message += `DUOMENYS:\n`;

  // Handle vehicle fields if present
  const hasVehicleFields = formData.plate || formData.vin;
  if (hasVehicleFields) {
    if (formData.plate) {
      message += `• ${fieldLabels.plate}: ${formData.plate}\n`;
    } else if (formData.vin) {
      message += `• ${fieldLabels.vin}: ${formData.vin}\n`;
    }
  }

  // Add all other relevant fields dynamically
  const fieldsToInclude = [
    'code',
    'startDate',
    'work_duration_years',
    'endDate',
    'registeredVehicle',
    'vehicle_motor_power_kw',
    'vehicle_engine_capacity_cc',
    'building_type',
    'construction_material',
    'construction_material_other',
    'work_value',
    'buildings_count',
    'construction_type',
    'technical_supervisor_certificate_number',
    'has_common_walls',
    'is_heritage_area',
    'includes_demolition',
    'travel_destinations',
    'travel_start_date',
    'travel_end_date',
    'travel_purpose',
    'travel_activity',
    'insured_travelers',
    'bicycle_value',
    'bicycle_custom_value',
    'additional_insurance_child_seat',
    'additional_insurance_family_members',
    'additional_insurance_europe_14_days',
    'additional_insurance_wheels_saddle',
  ];

  fieldsToInclude.forEach(field => {
    let shouldInclude = false;
    const fieldValue = formData[field];

    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      // For arrays, check if there are valid items
      if (Array.isArray(fieldValue)) {
        if (field === 'insured_travelers') {
          // Filter out empty values for insured_travelers
          const validItems = fieldValue.filter(t => t && t.trim() !== '');
          shouldInclude = validItems.length > 0;
        } else {
          shouldInclude = fieldValue.length > 0;
        }
      } else if (typeof fieldValue === 'boolean') {
        // For boolean fields (additional insurance checkboxes), only include if true
        shouldInclude = fieldValue === true;
      } else {
        shouldInclude = true;
      }
    }

    if (shouldInclude) {
      if (
        field === 'endDate' &&
        formData.work_duration_years &&
        (normalizedType === 'statybos' ||
          isConstructionCarSlug(insuranceTypeString))
      ) {
        return;
      }

      const label = fieldLabels[field] || field;
      let value = formatFieldValue(field, fieldValue);
      
      // Special handling for bicycle_value with custom value
      if (field === 'bicycle_value' && fieldValue === 'OTHER' && formData.bicycle_custom_value) {
        value = `${value} (${formData.bicycle_custom_value} €)`;
      }

      if (
        field === 'construction_material' &&
        fieldValue === 'OTHER' &&
        formData.construction_material_other
      ) {
        value = formData.construction_material_other;
      }

      // Skip bicycle_custom_value if it's already included in bicycle_value
      if (field === 'bicycle_custom_value' && formData.bicycle_value === 'OTHER') {
        return; // Skip this field as it's already included above
      }

      if (
        field === 'construction_material_other' &&
        formData.construction_material === 'OTHER'
      ) {
        return;
      }
      
      message += `• ${label}: ${value}\n`;
    }
  });

  const isKaskoCalculator =
    isKaskoInsuranceSlug(insuranceTypeString);
  const isTpvcaCalculator =
    isTpvcaMandatorySlug(insuranceTypeString);

  if (isKaskoCalculator || isTpvcaCalculator) {
    const usageSlug = isKaskoCalculator
      ? KASKO_INSURANCE_SLUG
      : TPVCA_MANDATORY_SLUG;

    const appendCarDetail = (label, value) => {
      if (value === null || value === undefined || value === '') return;
      const str =
        typeof value === 'boolean' ? (value ? 'Taip' : 'Ne') : String(value);
      message += `• ${label}: ${str}\n`;
    };

    if (formData.duration != null && formData.duration !== '') {
      appendCarDetail('Draudimo periodas (mėn.)', formData.duration);
    }

    if (formData.usagePurpose != null && formData.usagePurpose !== '') {
      appendCarDetail(
        'Naudojimo paskirtis',
        getVehicleUsageLabel(
          formData.usagePurpose,
          usageSlug,
          isCompanyCodeFromValue(formData.code)
        )
      );
    }

    if (formData.registrationCity) {
      appendCarDetail('Registracijos vieta', formData.registrationCity);
    }
    if (formData.registrationMunicipalityCode) {
      appendCarDetail(
        'Registracijos savivaldybės kodas',
        formData.registrationMunicipalityCode
      );
    }

    if (formData.drivingExperienceDate) {
      appendCarDetail(
        'Savininko vairavimo stažas nuo',
        formData.drivingExperienceDate
      );
    }

    if (formData.mainDeductable) {
      appendCarDetail('Pagrindinė išskaita (franšizė) (EUR)', formData.mainDeductable);
    }

    if (formData.theftDeductable != null && formData.theftDeductable !== '') {
      appendCarDetail('Vagystės išskaita (franšizė) (%)', formData.theftDeductable);
    }

    if (formData.paymentMethod) {
      const pm =
        formData.paymentMethod === 'installments'
          ? 'Mokėjimas dalimis'
          : formData.paymentMethod === 'annual'
            ? 'Metinė įmoka'
            : formData.paymentMethod;
      appendCarDetail('Mokėjimo būdas (skaičiuoklėje)', pm);
    }

    if (formData.otherDrivers === true) {
      appendCarDetail('Kiti vairuotojai', 'Taip');
      if (formData.otherDriverMinAge) {
        appendCarDetail('Kitų vairuotojų min. amžius', formData.otherDriverMinAge);
      }
      if (formData.otherDriverMinExperience) {
        appendCarDetail(
          'Kitų vairuotojų min. stažas (metai)',
          formData.otherDriverMinExperience
        );
      }
    } else if (formData.otherDrivers === false) {
      appendCarDetail('Kiti vairuotojai', 'Ne');
    }

    if (formData.isFromUSA === true) {
      appendCarDetail('Automobilis iš JAV', 'Taip');
      if (formData.vehicleManufacturingYear) {
        appendCarDetail(
          'Gamybos metai (JAV)',
          formData.vehicleManufacturingYear
        );
      }
    } else if (formData.isFromUSA === false) {
      appendCarDetail('Automobilis iš JAV', 'Ne');
    }

    if (formData.isNewCar === true) {
      appendCarDetail('Naujas automobilis', 'Taip');
      if (formData.carValue) {
        appendCarDetail('Automobilio vertė (EUR)', formData.carValue);
      }
    } else if (formData.isNewCar === false) {
      appendCarDetail('Naujas automobilis', 'Ne');
    }

    if (formData.newCarInsurance) {
      appendCarDetail(
        'Naujo automobilio draudimas (pasirinkimas)',
        formData.newCarInsurance
      );
    }

    if (formData.roadsideAssistanceRegion) {
      appendCarDetail(
        'Techninės pagalbos zona',
        formData.roadsideAssistanceRegion
      );
    }

    if (formData.wear_and_tear_coverage === true) {
      appendCarDetail('Nusidėvėjimo draudimas', 'Taip');
    }
    if (formData.repair_shop_choice_coverage === true) {
      appendCarDetail('Autoserviso pasirinkimas', 'Taip');
    }

    if (formData.isLeased === true) {
      appendCarDetail('Lizingas', 'Taip');
      if (formData.leasingCompanyName) {
        appendCarDetail('Lizingo įmonės pavadinimas', formData.leasingCompanyName);
      }
      if (formData.leasingCompanyCode) {
        appendCarDetail('Lizingo įmonės kodas', formData.leasingCompanyCode);
      }
    } else if (formData.isLeased === false) {
      appendCarDetail('Lizingas', 'Ne');
    }

    if (isKaskoCalculator && formData.kaskoPayerCategory) {
      appendCarDetail('Mokėtojo kategorija', formData.kaskoPayerCategory);
      if (formData.kaskoPayerEntityType === 'legal') {
        if (formData.kaskoPayerCompanyName) {
          appendCarDetail('Mokėtojas (įmonė)', formData.kaskoPayerCompanyName);
        }
        if (formData.kaskoPayerCompanyCode) {
          appendCarDetail(
            'Mokėtojo įmonės kodas',
            formData.kaskoPayerCompanyCode
          );
        }
      } else if (formData.kaskoPayerEntityType === 'natural') {
        const payerName = [
          formData.kaskoPayerFirstName,
          formData.kaskoPayerLastName,
        ]
          .filter(Boolean)
          .join(' ');
        if (payerName) {
          appendCarDetail('Mokėtojas (vardas, pavardė)', payerName);
        }
        if (
          !isKaskoCalculator &&
          formData.kaskoPayerPersonalCode
        ) {
          appendCarDetail(
            'Mokėtojo asmens kodas',
            formData.kaskoPayerPersonalCode
          );
        }
      }
    }

    const vehicleMeta =
      formData.carInfo ||
      formData.vehicleInfo ||
      formData.completedQuoteResponse?.captured_object_info ||
      formData.initialQuoteResponse?.captured_object_info;

    if (vehicleMeta && typeof vehicleMeta === 'object') {
      const m = vehicleMeta;
      if (m.make) appendCarDetail('Markė', m.make);
      if (m.model) appendCarDetail('Modelis', m.model);
      const year = m.manufacturing_year ?? m.year ?? m.manufacturingYear;
      if (year) appendCarDetail('Gamybos metai', year);
    }
  }

  // Handle nested policyholder fields
  if (formData.policyholder) {
    if (formData.policyholder.personal_code) {
      message += `• ${fieldLabels['policyholder.personal_code']}: ${formData.policyholder.personal_code}\n`;
    }
    if (formData.policyholder.company_code) {
      message += `• ${fieldLabels['policyholder.company_code']}: ${formData.policyholder.company_code}\n`;
    }
    if (formData.policyholder.company_name) {
      message += `• ${fieldLabels['policyholder.company_name']}: ${formData.policyholder.company_name}\n`;
    }
    if (formData.policyholder.first_name || formData.policyholder.last_name) {
      const name = [
        formData.policyholder.first_name,
        formData.policyholder.last_name,
      ]
        .filter(Boolean)
        .join(' ');
      if (name) message += `• Vardas, pavardė: ${name}\n`;
    }
    if (formData.policyholder.address) {
      const addr = formData.policyholder.address;
      if (addr.city)
        message += `• ${fieldLabels['policyholder.address.city']}: ${addr.city}\n`;
      if (addr.street)
        message += `• ${fieldLabels['policyholder.address.street']}: ${addr.street}\n`;
      if (addr.house_number)
        message += `• ${fieldLabels['policyholder.address.house_number']}: ${addr.house_number}\n`;
      if (addr.apartment_number)
        message += `• ${fieldLabels['policyholder.address.apartment_number']}: ${addr.apartment_number}\n`;
    }
  }

  const isRealEstateNt =
    insuranceTypeObj.isHomeInsuranceSlug(slug) ||
    insuranceTypeString === 'nt';
  if (isRealEstateNt) {
    const ntFields = [
      'residence_type',
      'rental_type',
      'insurance_variant',
      'is_interior_property_relevant',
      'is_internal_damages_needed',
      'is_smart_devices_relevant',
      'is_civil_liability_relevant',
    ];
    ntFields.forEach(field => {
      const raw = formData[field];
      if (raw === undefined || raw === null || raw === '') return;
      const label = fieldLabels[field] || field;
      const value = formatFieldValue(field, raw);
      message += `• ${label}: ${value}\n`;
    });
  }

  // Handle construction address fields
  if (formData.construction_address) {
    const constAddr = formData.construction_address;
    if (constAddr.city)
      message += `• ${fieldLabels['construction_address.city']}: ${constAddr.city}\n`;
    if (constAddr.street)
      message += `• ${fieldLabels['construction_address.street']}: ${constAddr.street}\n`;
    if (constAddr.house_number)
      message += `• ${fieldLabels['construction_address.house_number']}: ${constAddr.house_number}\n`;
  }

  // Handle file attachments
  if (
    formData.statybos_leidimas &&
    Array.isArray(formData.statybos_leidimas) &&
    formData.statybos_leidimas.length > 0
  ) {
    message += `• Statybos leidimas: ${formData.statybos_leidimas.length} failas(-ai)\n`;
  }
  if (
    formData.samata &&
    Array.isArray(formData.samata) &&
    formData.samata.length > 0
  ) {
    message += `• Sąmata: ${formData.samata.length} failas(-ai)\n`;
  }

  message += `\n`;

  if (userComments) {
    message += `PAPILDOMI KOMENTARAI:\n`;
    message += `${userComments}\n\n`;
  }

  message += `---\n`;
  message += `Šis laiškas buvo išsiųstas iš draudimo skaičiuoklės sistemos.`;

  return message;
};

/**
 * Formats insurance data for plain text (without HTML)
 * @param {Object} formData - The insurance form data object
 * @param {string} insuranceType - Type of insurance (auto, kasko, tpvca, etc.)
 * @param {string} userComments - Optional user comments to include
 * @returns {string} Formatted plain text string
 */
export const formatInsuranceDataForPlainText = (
  formData,
  insuranceType = 'quote_request',
  userComments = ''
) => {
  const insuranceTypeLabel =
    insuranceType === 'auto'
      ? 'Automobilių draudimas'
      : insuranceType === 'kasko'
        ? 'KASKO draudimas'
        : insuranceType === 'tpvca'
          ? 'TPVCA draudimas'
          : 'Draudimo užklausa';

  let message = `${insuranceTypeLabel} - Užklausa\n\n`;
  message += `Transporto priemonės duomenys:\n`;

  // Handle both plate number and VIN number
  if (formData.plate) {
    message += `- Valstybinis numeris: ${formData.plate}\n`;
  } else if (formData.vin) {
    message += `- Identifikavimo numeris (VIN): ${formData.vin}\n`;
  } else {
    message += `- Valstybinis numeris / VIN: Nenurodytas\n`;
  }

  message += `- Savininko asmens/įmonės kodas: ${formData.code || 'Nenurodytas'}\n`;
  message += `- Draudimo įsigaliojimo data: ${formData.startDate || 'Nenurodyta'}\n`;
  message += `- Registruota transporto priemonė: ${formData.registeredVehicle ? 'Taip' : 'Ne'}\n\n`;

  if (userComments) {
    message += `Papildomi komentarai:\n${userComments}\n\n`;
  }

  message += `Šis laiškas buvo išsiųstas iš draudimo skaičiuoklės sistemos.`;

  return message;
};
